import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Truck, Package, Globe, Users, Warehouse, Zap, 
  ArrowRight, CheckCircle, Star, Award, ChevronRight,
  Phone, Mail, Clock, MapPin
} from 'lucide-react';
import { services, companyInfo, heroImages } from '../data/mock';
import lepusTruck from '../assets/images/lepus-group-truck.jpeg';

// Clean animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const ServiceCard = ({ service, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [isHovered, setIsHovered] = useState(false);

  const IconComponent = {
    Truck, Package, Globe, Users, Warehouse, Zap
  }[service.icon] || Truck;

  const backgroundImages = [
    heroImages.truck1,
    heroImages.truck2,
    heroImages.truck3,
    heroImages.transportation1,
    heroImages.transportation2,
    heroImages.warehouse
  ];

  return (
    <motion.div
      ref={ref}
      variants={fadeIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay: index * 0.1 }}
      className="h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#1d526d]/20 h-full flex flex-col group hover:-translate-y-1">
        
        {/* Service Image with Overlay */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={backgroundImages[index % backgroundImages.length]}
            alt={service.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          
          {/* Service Icon */}
          <motion.div
            className="absolute top-4 left-4 w-14 h-14 bg-gradient-to-br from-[#1d526d] to-[#2a6b89] rounded-xl flex items-center justify-center shadow-lg"
            whileHover={{ scale: 1.1, rotate: 8 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <IconComponent className="w-7 h-7 text-white" />
          </motion.div>

          {/* Premium Badge */}
          <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1">
            <div className="flex items-center text-xs text-gray-700">
              <Star className="w-3 h-3 text-yellow-500 mr-1" />
              <span className="font-bold">Premium</span>
            </div>
          </div>
        </div>

        {/* Service Content */}
        <div className="p-6 lg:p-8 flex-1 flex flex-col">
          <h3 className="text-2xl font-bold text-black mb-4 group-hover:text-[#1d526d] transition-colors duration-300">
            {service.title}
          </h3>
          
          <p className="text-gray-600 mb-6 leading-relaxed text-lg flex-grow">
            {service.shortDescription}
          </p>

          {/* Key Features */}
          <div className="space-y-3 mb-6">
            {service.features.slice(0, 3).map((feature, idx) => (
              <div key={idx} className="flex items-start text-gray-700">
                <CheckCircle className="w-5 h-5 text-[#1d526d] mr-3 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed font-medium">{feature}</span>
              </div>
            ))}
          </div>

          {/* Industries Served */}
          <div className="mb-6">
            <div className="text-sm text-[#1d526d] font-bold mb-2">Industries Served:</div>
            <div className="flex flex-wrap gap-2">
              {service.industries.slice(0, 3).map((industry, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full font-medium"
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="mt-auto"
          >
            <Link
              to="/contact"
              className="w-full bg-[#1d526d] hover:bg-black text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 flex items-center justify-center group shadow-lg"
            >
              <span>Get Quote</span>
              <motion.div
                animate={{ x: isHovered ? 5 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronRight className="w-5 h-5 ml-2" />
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const Services = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const features = [
    {
      icon: Clock,
      title: "24/7 Operations",
      description: "Round-the-clock dispatch and customer support"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Experienced drivers and logistics professionals"
    },
    {
      icon: Award,
      title: "98.5% On-Time",
      description: "Industry-leading on-time delivery rate"
    },
    {
      icon: CheckCircle,
      title: "Damage-Free",
      description: "Secure handling and professional service"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Clean & Modern */}
      <section className="relative min-h-screen bg-gradient-to-br from-gray-50 to-white overflow-hidden pt-4 sm:pt-6 lg:pt-8">

        {/* Background Image */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src={lepusTruck}
            alt="Transportation Services" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/95"></div>
        </div>

        {/* Clean Geometric Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#1d526d]/5 to-transparent rounded-full blur-2xl"
          />
          <motion.div
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-br from-gray-200/20 to-transparent rounded-full blur-xl"
          />
        </div>

        <div ref={heroRef} className="relative z-10 min-h-screen flex items-center py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <div className="text-center">
              <motion.h1
                variants={slideIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-5xl lg:text-6xl font-bold text-black mb-8"
              >
                Our
                <span className="block text-[#1d526d] mt-2">Services</span>
              </motion.h1>
              
              <motion.p
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed"
              >
                Comprehensive transportation solutions tailored to your business needs. 
                From full truckload to specialized logistics, we deliver excellence.
              </motion.p>

              <motion.div
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="flex flex-col sm:flex-row gap-6 justify-center"
              >
                <motion.a 
                  href="#services"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#1d526d] hover:bg-black text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl inline-flex items-center justify-center group"
                >
                  Explore Services
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </motion.a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">
              Why Choose Our Services?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Experience the difference with our comprehensive transportation solutions 
              backed by industry-leading service and reliability.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="text-center"
              >
                <motion.div
                  className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-r from-[#1d526d] to-[#2a6b89] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <feature.icon className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                </motion.div>
                <h3 className="text-lg lg:text-xl font-bold text-black mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">
              Transportation Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Discover our comprehensive range of transportation services designed to meet 
              your specific logistics needs with professional excellence.
            </p>
          </motion.div>
          
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-[#1d526d] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={lepusTruck}
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-12 leading-relaxed max-w-4xl mx-auto">
              Experience the {companyInfo.name} difference. Contact us today for a customized 
              transportation solution that meets your specific business needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/contact"
                  className="bg-white text-[#1d526d] px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center justify-center group shadow-2xl"
                >
                  <Phone className="w-5 h-5 mr-3" />
                  Get Quote Now
                </Link>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/contact"
                  className="border-2 border-white text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-[#1d526d] transition-all duration-300 flex items-center justify-center"
                >
                  <Mail className="w-5 h-5 mr-3" />
                  Contact Us
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;